/* eslint-disable lingui/no-unlocalized-strings */

import {
  CaretDown,
  ChatTeardropText,
  CircleNotch,
  Exam,
  ListBullets,
  MagicWand,
  PenNib,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";

import { toast } from "../hooks/use-toast";
import { changeTone } from "../services/openai/change-tone";
import { convertToBullets } from "../services/openai/convert-to-bullets";
import { fixGrammar } from "../services/openai/fix-grammar";
import { improveWriting } from "../services/openai/improve-writing";

type Action = "improve" | "fix" | "tone" | "bullets";
type Mood = "casual" | "professional" | "confident" | "friendly";

type Props = {
  value: string;
  content?: string;
  onChange: (value: string) => void;
  className?: string;
};

export const AiActions = ({ value, content, onChange, className }: Props) => {
  const [loading, setLoading] = useState<Action | false>(false);
  const [completedAction, setCompletedAction] = useState<Action | null>(null);
  const aiEnabled = true;
  // useOpenAiStore((state) => !!state.apiKey);

  if (!aiEnabled) return null;

  const escapeHtml = (text: string) =>
    text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const looksLikeBullets = (text: string) => {
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length < 2) return false;

    return lines.some((line) => /^(?:[-*]|\u2022)\s+/.test(line) || /^\d+[.)]\s+/.test(line));
  };

  const normalizeBulletLines = (text: string) => {
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) =>
        line
          .replace(/^(?:[-*]|\u2022)\s+/, "")
          .replace(/^\d+[.)]\s+/, "")
          .trim(),
      )
      .filter(Boolean);

    if (lines.length >= 2) return lines;

    return text
      .replace(/\s+/g, " ")
      .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
      .map((line) => line.trim())
      .filter(Boolean);
  };

  const toBulletListHtml = (text: string) => {
    const items = normalizeBulletLines(text);

    if (items.length === 0) return text;
    if (items.length === 1) return `<p>${escapeHtml(items[0])}</p>`;

    return `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`;
  };

  const onClick = async (action: Action, mood?: Mood) => {
    try {
      setLoading(action);

      let result = value;
      const shouldPreserveBullets = looksLikeBullets(value);

      if (action === "improve") {
        result = await improveWriting(value);
        setCompletedAction("improve");
      }
      if (action === "fix") {
        result = await fixGrammar(value);
        setCompletedAction("fix");
      }
      if (action === "tone" && mood) {
        result = await changeTone(value, mood);
        setCompletedAction("tone");
      }
      if (action === "bullets") {
        result = await convertToBullets(value);
        setCompletedAction("bullets");
      }

      const nextContent =
        action === "bullets" || shouldPreserveBullets ? toBulletListHtml(result) : result;

      if (nextContent === content) return;

      onChange(nextContent);
    } catch (error) {
      toast({
        variant: "error",
        title: "Oops, the server returned an error.",
        description: (error as Error)?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "relative mt-4 rounded bg-secondary-accent/50 p-3 outline outline-secondary-accent",
        "flex flex-wrap items-center justify-center gap-2",
        className,
      )}
    >
      <div className="absolute -left-5 z-10">
        <Badge
          outline
          variant="primary"
          className="-rotate-90 bg-background px-2 text-[10px] leading-[10px]"
        >
          <MagicWand size={10} className="mr-1" />
          AI
        </Badge>
      </div>

      <Button size="sm" variant="outline" disabled={!!loading} onClick={() => onClick("improve")}>
        {loading === "improve" ? <CircleNotch className="animate-spin" /> : <PenNib />}
        <span className="ml-2 text-xs">Improve Writing</span>
      </Button>

      <Button size="sm" variant="outline" disabled={!!loading} onClick={() => onClick("fix")}>
        {loading === "fix" ? <CircleNotch className="animate-spin" /> : <Exam />}
        <span className="ml-2 text-xs">Fix Spelling & Grammar</span>
      </Button>

      <Button size="sm" variant="outline" disabled={!!loading} onClick={() => onClick("bullets")}>
        {loading === "bullets" ? <CircleNotch className="animate-spin" /> : <ListBullets />}
        <span className="ml-2 text-xs">Convert to Bullets</span>
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" disabled={!!loading}>
            {loading === "tone" ? <CircleNotch className="animate-spin" /> : <ChatTeardropText />}
            <span className="mx-2 text-xs">Change Tone</span>
            <CaretDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => onClick("tone", "casual")}>
            <span role="img" aria-label="Casual">
              🙂
            </span>
            <span className="ml-2">Casual</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "professional")}>
            <span role="img" aria-label="Professional">
              💼
            </span>
            <span className="ml-2">Professional</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "confident")}>
            <span role="img" aria-label="Confident">
              😎
            </span>
            <span className="ml-2">Confident</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "friendly")}>
            <span role="img" aria-label="Friendly">
              😊
            </span>
            <span className="ml-2">Friendly</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog component */}
      <Dialog open={!!completedAction} onOpenChange={(open) => !open && setCompletedAction(null)}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                width="24"
                height="24"
                viewBox="0 0 48 48"
              >
                <path
                  fill="#c8e6c9"
                  d="M44,24c0,11-9,20-20,20S4,35,4,24S13,4,24,4S44,13,44,24z"
                ></path>
                <polyline
                  fill="none"
                  stroke="#4caf50"
                  stroke-miterlimit="10"
                  stroke-width="4"
                  points="14,24 21,31 36,16"
                ></polyline>
              </svg>
              <DialogTitle>
                {completedAction === "fix" && "Grammar Check Complete"}
                {completedAction === "improve" && "Writing Improved"}
                {completedAction === "tone" && "Tone Changed"}
                {completedAction === "bullets" && "Bullets Created"}
              </DialogTitle>
            </div>
            <DialogDescription>
              {completedAction === "fix" &&
                "Your text has been successfully checked and corrected."}
              {completedAction === "improve" &&
                "Your writing has been enhanced for better clarity and impact."}
              {completedAction === "tone" &&
                "The tone of your text has been successfully adjusted."}
              {completedAction === "bullets" &&
                "Your text has been converted into resume-style bullet points."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setCompletedAction(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
